import React, {useEffect, useState, useRef} from 'react';
import {useLocation, useNavigate} from 'react-router-dom';
import styles from './MobileMenu.module.css';
import logo_svg from "../static_files/kiraAi logo.svg";

import dashboardImg from '../static_files/dashboard.svg';
import dashboardWhiteImg from '../static_files/dashboardwhite.svg';
import myAssetsImg from '../static_files/myassets.svg';
import myAssetsWhiteImg from '../static_files/myassetswhite.svg';
import transactionsImg from '../static_files/transaction.svg';
import transactionsWhiteImg from '../static_files/transactionswhite.svg';
import analyticsImg from '../static_files/analytics.svg';
import analyticsWhiteImg from '../static_files/analyticswhite.svg';
import docsImg from '../static_files/docs.svg';
import {DOCS_URL} from '../utils';

function SideMenu({isOpen, onOverlayClick, stopPropagation}) {
    const location = useLocation();
    const navigate = useNavigate();
    const [isRendered, setIsRendered] = useState(false);
    const [imagesLoaded, setImagesLoaded] = useState(false);
    const imagesRef = useRef({});

    // 预加载图像
    useEffect(() => {
        const preloadImages = () => {
            const images = [
                dashboardImg, dashboardWhiteImg, myAssetsImg, myAssetsWhiteImg,
                transactionsImg, transactionsWhiteImg, analyticsImg, analyticsWhiteImg,
                docsImg
            ];

            let loadedCount = 0;
            const totalImages = images.length;

            images.forEach(src => {
                if (imagesRef.current[src]) {
                    // 已经加载过的图像直接计数
                    loadedCount++;
                    if (loadedCount === totalImages) {
                        setImagesLoaded(true);
                    }
                } else {
                    const img = new Image();
                    img.onload = () => {
                        loadedCount++;
                        imagesRef.current[src] = true;
                        if (loadedCount === totalImages) {
                            setImagesLoaded(true);
                        }
                    };
                    img.onerror = () => {
                        loadedCount++;
                        if (loadedCount === totalImages) {
                            setImagesLoaded(true);
                        }
                    };
                    img.src = src;
                }
            });
        };

        // 立即开始预加载，无论菜单是否打开
        preloadImages();
    }, []);

    // 确保即使菜单关闭时也渲染DOM，以便可以预加载内容
    useEffect(() => {
        if (isOpen) {
            setIsRendered(true);
        }
    }, [isOpen]);

    // 当菜单关闭后延迟移除DOM
    useEffect(() => {
        if (!isOpen && isRendered) {
            const timer = setTimeout(() => {
                setIsRendered(false);
            }, 500); // 延长一点以确保动画完成

            return () => clearTimeout(timer);
        }
    }, [isOpen, isRendered]);

    // Same logic as in LeftPanel for My Assets
    const handleMyAssetsClick = () => {
        const token = localStorage.getItem('gatewayToken');
        if (token) {
            navigate(`/swap?token=${token}`);
        } else {
            navigate('/');
        }
        onOverlayClick();
    };

    const handleTransactionsClick = () => {
        navigate('/transaction');
        onOverlayClick();
    };

    const isMyAssetsActive =
        location.pathname === '/' || location.pathname.startsWith('/swap');
    const isDashboardActive = location.pathname === '/dashboard';
    const isTransactionsActive = location.pathname === '/transaction';
    const isAnalyticsActive = location.pathname === '/analytics';
    const isDocsActive = location.pathname === '/docs';

    // 菜单未打开时不渲染任何内容
    if (!isOpen && !isRendered) {
        return null;
    }

    // 准备菜单渲染类
    const menuContainerClass = `${styles.sideMenuContainer} ${isOpen ? styles.open : ''} ${imagesLoaded ? styles.imagesLoaded : ''}`;

    return (
        <>
            <div
                className={menuContainerClass}
                onClick={stopPropagation}
            >
                <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    height: '32px',
                    width: '100%',
                    marginBottom: '32px'
                }}>
                    <img
                        src={logo_svg}
                        style={{height: '34px', marginLeft: "8px"}}
                        alt="KiraAI"
                    />
                    <button className={styles.closeButton} onClick={onOverlayClick}>
                        ✕
                    </button>
                </div>
                <div>
                    {/* Dashboard */}
                    <div
                        className={`${styles.menuItem} ${
                            isDashboardActive ? styles.selected : ''
                        } ${imagesLoaded ? styles.imageLoaded : ''}`}
                        onClick={() => {
                            navigate('/dashboard');
                            onOverlayClick();
                        }}
                    >
                        <div className={styles.imageContainer}>
                            <img
                                src={dashboardImg}
                                alt="Dashboard"
                            />
                        </div>
                        <span>Dashboard</span>
                    </div>

                    {/* My Assets */}
                    <div
                        className={`${styles.menuItem} ${
                            isMyAssetsActive ? styles.selected : ''
                        } ${imagesLoaded ? styles.imageLoaded : ''}`}
                        onClick={handleMyAssetsClick}
                    >
                        <div className={styles.imageContainer}>
                            <img
                                src={myAssetsImg}
                                alt="My Assets"
                            />
                        </div>
                        <span>My Assets</span>
                    </div>

                    {/* Transactions */}
                    <div
                        className={`${styles.menuItem} ${
                            isTransactionsActive ? styles.selected : ''
                        } ${imagesLoaded ? styles.imageLoaded : ''}`}
                        onClick={handleTransactionsClick}
                    >
                        <div className={styles.imageContainer}>
                            <img
                                src={transactionsImg}
                                alt="Transactions"
                            />
                        </div>
                        <span>Transactions</span>
                    </div>

                    {/* Analytics */}
                    <div
                        className={`${styles.menuItem} ${
                            isAnalyticsActive ? styles.selected : ''
                        } ${imagesLoaded ? styles.imageLoaded : ''}`}
                        onClick={() => {
                            navigate('/analytics');
                            onOverlayClick();
                        }}
                    >
                        <div className={styles.imageContainer}>
                            <img
                                src={analyticsImg}
                                alt="Analytics"
                            />
                        </div>
                        <span>Analytics</span>
                    </div>

                    {/* Docs */}
                    <div
                        className={`${styles.menuItem} ${
                            isDocsActive ? styles.selected : ''
                        } ${imagesLoaded ? styles.imageLoaded : ''}`}
                        onClick={() => {
                            window.open(DOCS_URL, '_blank', 'noopener,noreferrer');
                            onOverlayClick();
                        }}
                    >
                        <div className={styles.imageContainer}>
                            <img src={docsImg} alt="Docs"/>
                        </div>
                        <span>Docs</span>
                    </div>
                </div>
            </div>
        </>
    );
}

export default SideMenu;
